import { useHoverPopover } from '@app/core/hooks';
import { useRouter } from '@mi-front/core';
import { Box, Collapse, List } from '@mui/material';
import { useSidenavContext } from '../SidenavContext';
import { SIDENAV_TOKENS } from '../sidenav-tokens';
import { NavItem } from './NavItem';
import { NavItemChild } from './NavItemChild';
import { NavItemExpandChevron } from './NavItemExpandChevron';
import { PopoverTitle } from './PopoverTitle';
import { SidenavPopover } from './SidenavPopover';
import { useNavItemExpanded } from './useNavItemExpanded';

export type NavItemChildConfig = {
  label: string;
  to: string;
  isActive?: (pathname: string) => boolean;
  icon?: React.ReactNode;
};

export type NavItemWithChildrenProps = {
  root: string;
  label: string;
  icon?: React.ReactNode;
  items?: NavItemChildConfig[];
  /** Extra path prefixes that should keep this section expanded and highlighted as active. */
  additionalRoots?: string[];
};

export function NavItemWithChildren({
  label,
  icon,
  root,
  items = [],
  additionalRoots = [],
}: NavItemWithChildrenProps) {
  const { location } = useRouter();
  const { collapsed } = useSidenavContext();
  const hasChildItems = items.length > 0;
  // Leaf entries (e.g. Compose) rely on NavItem's own collapsed label popover.
  // Parents with children own the flyout menu so hover stays on the wrapper Box.
  const {
    anchorEl,
    menuOpen,
    handleMouseEnter,
    handleMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    closePopover,
  } = useHoverPopover({ enabled: collapsed && hasChildItems });

  const isActive = [root, ...additionalRoots].some((path) =>
    new RegExp(`^/${path.replace(/^\//, '')}`).test(location.pathname),
  );

  if (!root && items.length === 0) {
    throw new Error('Either to or items must be provided');
  }

  // When there are items, parent link is the first item's target (hub); otherwise use section/station slug
  const to = items.length > 0 ? items[0].to : (root ?? '#');

  const { expanded, toggleExpanded } = useNavItemExpanded(isActive);

  /**
   * When the item has sub-items, clicking the parent must not navigate.
   * Expanded: toggle the inline children list.
   * Collapsed: no-op — links live in the hover popover.
   * Leaf items navigate normally; NavItem closes its label popover on click.
   */
  const handleParentClick = (e: React.MouseEvent) => {
    if (!hasChildItems) {
      return;
    }
    e.preventDefault();
    if (!collapsed) {
      toggleExpanded();
    }
  };

  /** Renders child links for the inline collapse or the collapsed flyout. */
  const renderChildItems = (inPopover: boolean) =>
    items.map((child, index) => {
      const childProps = {
        label: child.label,
        to: child.to,
        icon: child.icon,
        inPopover,
        isActive: child.isActive
          ? (_match: unknown, location: { pathname: string }) => child.isActive!(location.pathname)
          : undefined,
      };

      // Popover wraps each child so a click closes the flyout after navigation starts.
      if (inPopover) {
        return (
          <Box key={index} onClick={closePopover}>
            <NavItemChild {...childProps} />
          </Box>
        );
      }

      return <NavItemChild key={index} {...childProps} />;
    });

  return (
    <>
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ display: 'block' }}
      >
        <NavItem
          to={to}
          icon={icon}
          hasChildren={hasChildItems}
          isActive={(match, location) => isActive}
          onClick={handleParentClick}
        >
          {hasChildItems ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: 1,
              }}
            >
              <Box component="span" sx={{ minWidth: 0 }}>
                {label}
              </Box>
              {!collapsed && <NavItemExpandChevron expanded={expanded} />}
            </Box>
          ) : (
            label
          )}
        </NavItem>
      </Box>
      {hasChildItems && !collapsed && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pb: SIDENAV_TOKENS.openMenuGap }}>
            {renderChildItems(false)}
          </List>
        </Collapse>
      )}
      {collapsed && hasChildItems && (
        <SidenavPopover
          open={menuOpen}
          anchorEl={anchorEl}
          onClose={closePopover}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          <PopoverTitle label={label} showBorder />
          <List component="div" disablePadding>
            {renderChildItems(true)}
          </List>
        </SidenavPopover>
      )}
    </>
  );
}
